import { useParams, Link } from 'react-router-dom';
import { articlePreviews, articleMap } from '@/mocks/articles';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useState, useEffect } from 'react';
import SEOHead from '@/components/base/SEOHead';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuy.living';

  // Scroll to top on article navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Look up article dynamically
  const article = slug ? articleMap[slug] : undefined;

  // Build JSON-LD for the article
  const articleJsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Article',
            headline: article.title,
            description: article.metaDescription,
            datePublished: article.date,
            author: {
              '@type': 'Organization',
              name: 'Superbuy Spreadsheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Superbuy Spreadsheet',
              url: siteUrl,
            },
            url: `${siteUrl}/article/${article.slug}`,
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.category,
                item: `${siteUrl}/article/${article.slug}`,
              },
            ],
          },
          ...(article.faqs.length > 0
            ? [
                {
                  '@type': 'FAQPage',
                  mainEntity: article.faqs.map((faq) => ({
                    '@type': 'Question',
                    name: faq.question,
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: faq.answer,
                    },
                  })),
                },
              ]
            : []),
        ],
      }
    : undefined;

  if (!article) {
    return (
      <>
        <SEOHead
          title="Article Not Found | Superbuy Spreadsheet"
          description="The article you are looking for does not exist. Explore our collection of shopping guides, safety tips, and product recommendations."
          canonicalUrl={`${siteUrl}/article/${slug || ''}`}
        />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center px-6">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="font-body text-foreground-500 mb-6">The article you are looking for does not exist yet. Explore our other guides below.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-label text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
              <Link to="/article/ultimate-superbuy-spreadsheet-guide-2026" className="px-6 py-3 border-2 border-primary-300 text-primary-700 font-label text-sm font-semibold rounded-full hover:bg-primary-100 transition-all duration-200 cursor-pointer whitespace-nowrap">
                Read Ultimate Guide
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articlePreviews.filter(
    (a) => article.relatedSlugs.includes(a.slug)
  );

  return (
    <>
      <SEOHead
        title={`${article.title} | Superbuy Spreadsheet`}
        description={article.metaDescription}
        keywords={article.metaKeywords}
        canonicalUrl={`${siteUrl}/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.metaDescription}
        ogUrl={`${siteUrl}/article/${article.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <Navbar />
      <main>
        {/* Article Hero */}
        <section className="pt-28 pb-10 md:pt-32 md:pb-14 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-6 md:px-10">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 font-body text-sm text-foreground-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600">{article.category}</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium truncate">{article.title}</span>
            </nav>

            <p className="font-label text-xs md:text-sm uppercase tracking-[0.2em] text-accent-500 mb-3">
              {article.category}
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-[1.15] mb-4">
              {article.title}
            </h1>
            <p className="font-body text-sm text-foreground-400">{article.date}</p>
          </div>
        </section>

        {/* Article Featured Image */}
        <section className="pb-8 md:pb-12 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-6 md:px-10">
            <div className="w-full aspect-[2/1] rounded-2xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-8 md:py-12 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-6 md:px-10">
            {article.sections.map((section, idx) => {
              // Handle comparison tables
              if (section.isComparisonTable && section.tableData) {
                const { headers, rows, highlightCol } = section.tableData;
                return (
                  <div key={idx} className="mb-10">
                    {section.heading && (
                      <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5">
                        {section.heading}
                      </h3>
                    )}
                    <div className="overflow-x-auto rounded-xl border border-background-200/70">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="bg-background-100">
                            {headers.map((h, hi) => (
                              <th
                                key={hi}
                                className={`px-5 py-4 font-label text-sm font-semibold whitespace-nowrap ${hi === highlightCol ? 'text-primary-700' : 'text-foreground-800'}`}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-background-200/50">
                          {rows.map((row, ri) => (
                            <tr key={ri} className={ri % 2 === 1 ? 'bg-background-100/50' : ''}>
                              {row.map((cell, ci) => (
                                <td
                                  key={ci}
                                  className={`px-5 py-3.5 font-body text-sm ${ci === highlightCol ? 'text-primary-700 font-medium' : 'text-foreground-700'}`}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }

              // Regular sections
              return (
                <div key={idx} className="mb-10">
                  {section.heading && section.headingLevel === 'h2' ? (
                    <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-semibold text-foreground-950 mb-4">
                      {section.heading}
                    </h2>
                  ) : section.heading && section.headingLevel === 'h3' ? (
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-4">
                      {section.heading}
                    </h3>
                  ) : null}
                  {section.content && (
                    <p className="font-body text-base text-foreground-700 leading-relaxed mb-4">
                      {section.content}
                    </p>
                  )}
                </div>
              );
            })}

            {/* FAQ Section within article */}
            {article.faqs.length > 0 && (
              <div className="mt-14 mb-10">
                <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-semibold text-foreground-950 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {article.faqs.map((faq, idx) => (
                    <div key={idx} className="bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                      <button
                        onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-200/30 transition-colors duration-200"
                      >
                        <span className="font-heading text-base font-semibold text-foreground-900 pr-4">
                          {faq.question}
                        </span>
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-background-50 flex items-center justify-center border border-background-300/60">
                          <i className={`ri-${openFAQ === idx ? 'subtract' : 'add'}-line text-xs text-foreground-600`}></i>
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${openFAQ === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                        <p className="px-5 pb-4 font-body text-sm text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Internal CTA */}
            <div className="my-12 p-8 bg-primary-50 rounded-2xl border border-primary-200/50 text-center">
              <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                Ready to Start Shopping?
              </h3>
              <p className="font-body text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                Explore our curated categories and discover thousands of handpicked products with real QC photos and trusted seller links.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-3 bg-primary-500 text-background-50 font-label text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Back to Home
                </Link>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 border-2 border-primary-300 text-primary-700 font-label text-sm font-semibold rounded-full hover:bg-primary-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Visit Main Store
                </a>
              </div>
            </div>

            {/* Conclusion */}
            <div className="mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                Conclusion
              </h2>
              <p className="font-body text-base text-foreground-700 leading-relaxed mb-4">
                The Superbuy Spreadsheet continues to be the most comprehensive and reliable curated
                shopping resource available online. With 14 carefully organized categories, real QC
                photos, verified seller ratings, and weekly updates, it provides everything you need
                to shop smarter, safer, and more efficiently than ever before.
              </p>
              <p className="font-body text-base text-foreground-700 leading-relaxed">
                Visit our <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium underline">homepage</Link> to
                start browsing all categories, or head directly to the{' '}
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium underline">main store</a> to
                explore the full collection. Happy shopping and stay safe!
              </p>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-8 text-center">
                Related Reads
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    onClick={() => window.scrollTo(0, 0)}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:translate-y-[-4px] transition-all duration-300 cursor-pointer border border-background-200/60"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="px-2.5 py-1 bg-background-100 text-foreground-600 font-label text-xs font-medium rounded-full mb-2 inline-block">
                        {ra.category}
                      </span>
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-700 transition-colors duration-200">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}